import { FIGURES, isFigure } from './carFigures';
import { HEADLINE_SPECS } from './carSchema';

/**
 * Presenting the published figures (SPEC-002).
 *
 * The catalogue stores every figure in one unit — km/h, hp, Nm, kg, mm, cc — so
 * that cars are comparable; turning that back into something a human reads is
 * this module's job. It is pure, and it returns NUMBERS AND MESSAGE KEYS, never
 * a resolved string: a domain module that reached for `t()` would be a domain
 * module that had learned about the language on screen, and the first thing it
 * would do with that knowledge is ship "km/h" untranslated to every locale
 * added after this one.
 *
 * `Intl.NumberFormat` is the one exception, and it is not a contradiction:
 * grouping is a fact about a language, not copy — 1,100 in English is 1.100 in
 * German — and it is the standard library's job rather than a translator's.
 *
 * What is here is a figure and the shape a ROW of them takes. One particular
 * arrangement of rows — the twenty-three of the full specification — lives in
 * `specSheet.js`, which moved out when it grew past being a list; the other is
 * the hero plate's four, which is short enough to stay.
 */

/** A figure the catalogue genuinely does not have. Never a zero, never a guess. */
export const EM_DASH = '—';

/** A range, which is a different dash and a deliberate one. */
const EN_DASH = '–';

export { HEADLINE_SPECS };

export function specLabelKey(spec) {
    return `cars.spec.${spec}`;
}

/** The hero plate gives each figure about 40px, so it needs a label that fits. */
export function specShortLabelKey(spec) {
    return `cars.spec.${spec}.short`;
}

export function specUnitKey(spec) {
    return `cars.spec.${spec}.unit`;
}

/**
 * The label for a figure that carries the date it was true on.
 *
 * A second key rather than a placeholder the undated catalogue entry could
 * ignore, because `interpolate` leaves an unfilled `{when}` on screen exactly as
 * it found it. Two keys means the row with nothing to say picks the label that
 * does not ask.
 */
export function specDatedLabelKey(spec) {
    return `cars.spec.${spec}.dated`;
}

export function formatSpecNumber(value, { languageCode = 'en', fractionDigits = 0, grouped = true } = {}) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return EM_DASH;
    }

    return new Intl.NumberFormat(languageCode, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
        useGrouping: grouped,
    }).format(value);
}

/**
 * The years a car was built.
 *
 * One year rather than "1990–1990" where a run began and ended inside a year,
 * and one year again where only the first end is known — a range with a blank
 * side reads as a missing figure rather than as an open one.
 */
export function formatYearRange(years, { languageCode = 'en' } = {}) {
    const [from, to] = Array.isArray(years) ? years : [];
    // A year is a NAME for a year, not a quantity of them: "1,987" is the one
    // grouped number in this module that would be wrong in every language.
    const year = (value) => formatSpecNumber(value, { languageCode, grouped: false });
    const first = year(from);
    const last = year(to);

    if (first === EM_DASH) {
        return EM_DASH;
    }

    return last === EM_DASH || last === first ? first : `${first}${EN_DASH}${last}`;
}

/**
 * One figure, formatted, in the same shape the spec sheet's rows carry.
 *
 * The single entry point for "show me this car's `power`" outside this module,
 * so a screen that is not the spec table still gets the em dash, the grouping
 * and the unit-suppression rules rather than reinventing two of the three.
 *
 * Answers null for a figure the table does not address, rather than throwing on
 * a lookup that came back undefined: the callers outside the sheet compose their
 * own lists of figures, and one typo in such a list should cost a row.
 */
export function specFigure(car, spec, { languageCode = 'en' } = {}) {
    return isFigure(spec) ? figureRow(car, spec, { languageCode }) : null;
}

/**
 * One row of the sheet, with every field it may carry stated.
 *
 * A factory rather than nine object literals, because the table renders in ONE
 * loop over rows it assumes are the same shape — a builder that simply left
 * `unitKey` out would hand the component `undefined`, which is falsy in exactly
 * the same way `null` is and would never be noticed until a row needed the
 * field. `specSheet.test.js` holds the key set; this is what makes it true.
 *
 * The fields, and why there are this many:
 *
 * - `value` is a formatted STRING and `valueKey` is a message key, and a row
 *   carries exactly one. A number the catalogue stores is the first; a word a
 *   translator owns — "Twin-turbocharged", "5-speed manual" — is the second.
 * - `valueValues` fills that message's placeholders. "5-speed manual" reorders
 *   in most languages, so the gear count cannot be concatenated at the call
 *   site; it goes in as a substitution and the whole phrase stays one string a
 *   translator can move around.
 * - `noteKey` is the qualifier that comes AFTER a figure — "@ 7,000 rpm", "@ 200
 *   km/h". It is a separate field rather than part of the value because the
 *   value is set in the numeric face and the note is not, and because the number
 *   the reader came for is the power, not the engine speed it is made at.
 * - `numeric` says whether the value is a QUANTITY. Almost every row is; the
 *   tyre make is not, and "Pirelli P Zero" set in a tabular-figures face with
 *   the horsepower above it is a brand name pretending to be a measurement.
 */
export function specRow({
    key,
    labelKey,
    labelValues = null,
    value = null,
    valueKey = null,
    valueValues = null,
    unitKey = null,
    noteKey = null,
    noteValues = null,
    numeric = true,
}) {
    return { key, labelKey, labelValues, value, valueKey, valueValues, unitKey, noteKey, noteValues, numeric };
}

export function figureRow(car, spec, { languageCode = 'en' } = {}) {
    const { valueOf, fractionDigits = 0, hasUnit = true } = FIGURES[spec];
    const value = formatSpecNumber(valueOf(car?.specs), { languageCode, fractionDigits });

    return specRow({
        key: spec,
        labelKey: specLabelKey(spec),
        value,
        // No unit beside a figure that is not there: "— kg" is not a weight, it
        // is a unit with nothing in front of it.
        unitKey: hasUnit && value !== EM_DASH ? specUnitKey(spec) : null,
    });
}

/**
 * The four figures of the hero plate, in the schema's order.
 *
 * Always four, even where the catalogue is missing one: the plate is a
 * four-column grid and a car that dropped a column would be drawn differently
 * from every other card in the deck.
 */
export function headlineSpecs(car, { languageCode = 'en' } = {}) {
    return HEADLINE_SPECS.map((spec) => {
        const { unitKey, value } = figureRow(car, spec, { languageCode });
        const { valueOf, fractionDigits = 0 } = FIGURES[spec];
        const amount = valueOf(car?.specs);

        return {
            key: spec,
            labelKey: specLabelKey(spec),
            shortLabelKey: specShortLabelKey(spec),
            unitKey,
            value,
            // The figure as a NUMBER as well as as a string, so the plate can
            // roll from the last car's to this one's rather than cutting between
            // them (SPEC-004 D-61). `value` stays the authority on how it is
            // printed — the grouping and the decimal are facts about a language
            // — and this is only what the rolling is done over.
            //
            // Null rather than NaN for a figure the catalogue does not have, so
            // a caller can tell "no number" from "a number I could not read"
            // without knowing which of the two `formatSpecNumber` printed.
            amount: Number.isFinite(amount) ? amount : null,
            // How precise that number is. A roll has to round somewhere, and
            // rounding a 0-100 time to whole seconds would count 4.1 as 4.
            fractionDigits,
        };
    });
}
